<script>
import RexSearchSelect from '@/rex-components/RexSearchSelect.vue';
import RexModalLabel from '@/rex-components/rex-modal-label.vue';
import {
  MAX_INSTANCE_SELECTED,
  MQ_INSTANCE_LIMIT,
  MQ_INSTANCE_LABEL_MAPPING,
  MQ_INSTANCE_SEARCH_TXT_MAPPING,
} from '@/components/AlertsV2/constants';
import { mapActions, mapGetters } from 'vuex';
import { notificationError } from '@/utils/notification';

export default {
  emits: ['onMultiSelect'],
  components: { RexSearchSelect, RexModalLabel },
  props: {
    currentMetricObj: {
      type: Object,
      default: () => ({}),
    },
    selectedFields: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapGetters('alertV2State/configure', ['getCurrentAlertApplication']),
    getLabelHeader() {
      return MQ_INSTANCE_LABEL_MAPPING[this.type];
    },
    getSearchText() {
      return MQ_INSTANCE_SEARCH_TXT_MAPPING[this.type];
    },
    getMultiSelectedText() {
      const selectedLen = this.selectedInstancesKeys.length;
      const selectedKey =
        selectedLen === 1
          ? `alert.${this.type}Selected`
          : `alert.${this.type}sSelected`;

      return selectedLen === 0
        ? this.$t('alert.selectFromAvailable')
        : this.$t(selectedKey, [selectedLen]);
    },
  },
  data() {
    return {
      instances: [],
      selectedInstancesKeys: [],
      selectedInstances: [],
      type: this.currentMetricObj.category || '',
      isLoading: false,
    };
  },
  methods: {
    ...mapActions('mqState/mqCommonState', ['GET_MQ_ALERT_INSTANCE']),
    formatInstances(instanceArr) {
      return instanceArr.map((obj) => ({
        key: obj.label,
        label: obj.label,
        selected: this.selectedInstancesKeys?.includes(obj.label) || false,
      }));
    },
    async getActiveMQInstance() {
      try {
        const params = {
          serviceId: this.getCurrentAlertApplication?.key ?? '',
          properties: [{ key: 'instanceType', value: this.type }],
          keyword: '',
          limit: MQ_INSTANCE_LIMIT,
        };
        const instanceId = await this.GET_MQ_ALERT_INSTANCE(params);
        this.instances = this.formatInstances(instanceId);
        this.updateSelectedInstances();
      } catch (error) {
        console.log('error', error);
        this.instances = [];
        this.selectedInstancesKeys = [];
      }
    },
    handleMultiSelect(item) {
      if (
        this.selectedInstancesKeys?.length >= MAX_INSTANCE_SELECTED &&
        !item.selected
      ) {
        notificationError(this.$t('alert.maximum20InstancesCanBeSelected'));
        return;
      }
      this.instances = this.instances.map((obj) => {
        if (obj.key === item.key) obj.selected = !obj.selected;

        if (obj.selected && !this.selectedInstancesKeys.includes(obj.label)) {
          this.selectedInstances.push(obj);
          this.selectedInstancesKeys.push(obj.label);
        }

        if (!obj.selected && this.selectedInstancesKeys.includes(obj.label)) {
          this.selectedInstances = this.selectedInstances.filter(
            (inst) => inst.label !== obj.label,
          );
          this.selectedInstancesKeys = this.selectedInstancesKeys.filter(
            (key) => key !== obj.label,
          );
        }

        return obj;
      });
      this.$emit('onMultiSelect', this.selectedInstancesKeys);
    },
    async onInstanceSearch(search) {
      try {
        this.isLoading = true;
        const params = {
          serviceId: this.getCurrentAlertApplication?.key ?? '',
          properties: [{ key: 'instanceType', value: this.type }],
          keyword: search,
          limit: MQ_INSTANCE_LIMIT,
        };
        const instanceId = await this.GET_MQ_ALERT_INSTANCE(params);
        if (Array.isArray(instanceId)) {
          const updatedInstanceId = instanceId.map((inst) => ({
            key: inst.key,
            label: inst.label,
            selected: this.selectedInstancesKeys?.includes(inst.label) || false,
          }));
          this.instances = updatedInstanceId;
        }
        this.isLoading = false;
      } catch (error) {
        console.log('error', error);
        this.isLoading = false;
        this.instances = [];
      }
    },
    removeSelectedInstance(obj) {
      this.selectedInstances = this.selectedInstances.filter(
        (inst) => inst.label != obj.label,
      );

      this.selectedInstancesKeys = this.selectedInstances.map(
        (obj) => obj.label,
      );

      this.instances = this.instances.map((inst) => {
        if (inst.label === obj.label) {
          inst.selected = false;
        }
        return inst;
      });
      this.$emit('onMultiSelect', this.selectedInstancesKeys);
    },
    resetSelectedInstances() {
      this.instances = [];
      this.selectedInstancesKeys = [];
      this.selectedInstances = [];
    },
    updateSelectedInstances() {
      this.instances.forEach((obj) => {
        if (obj.selected) {
          this.selectedInstances.push(obj);
        }
      });
    },
  },
  mounted() {
    this.resetSelectedInstances();
    this.getActiveMQInstance();
  },
  watch: {
    currentMetricObj() {
      this.type = this.currentMetricObj.category;
      this.resetSelectedInstances();
      this.getActiveMQInstance();
    },
    selectedFields(newVal) {
      this.selectedInstancesKeys = newVal;
      this.instances.map((obj) => {
        obj.selected = newVal.includes(obj.key);
        return obj;
      });
    },
  },
};
</script>

<template>
  <div>
    <div class="active-mq-type-box">
      <RexModalLabel :primaryLabel="getLabelHeader + $t('asterisk')" />

      <RexSearchSelect
        :isEdit="true"
        :data="instances"
        :objectValueIndex="1"
        :boldActive="true"
        :isSearch="true"
        :searchPlaceholder="getSearchText"
        :multiSelect="true"
        id="alert-form-field-active-mq-type"
        @onMultiSelect="handleMultiSelect"
        :multiSelectTitle="getMultiSelectedText"
        :useParentData="true"
        :enableSearchBtn="true"
        @onSearch="onInstanceSearch"
        :isSearchLoading="isLoading"
        :enableSelectedList="true"
        :selectedList="selectedInstances"
        @onRemoveOption="removeSelectedInstance"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.active-mq-type-box {
  margin-top: 1.5rem;
}
.max-width {
  max-width: 20.75em !important;
}
</style>
